import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

export type MpStatus =
  | "idle"
  | "pending"
  | "approved"
  | "rejected"
  | "timeout"
  | "error";

export interface MpResult {
  monto: number;
  mp_payment_id?: string;
}

const POLL_INTERVAL_MS = 2000;
// 5 minutos / 2s = 150 intentos
const MAX_ATTEMPTS = 150;

interface QrCreateResponse {
  ext_ref?: string;
  external_reference?: string;
  qr_data?: string;
  status?: string;
  payment_id?: string | number;
}

interface QrValidateResponse {
  status: "pending" | "success" | "error" | string;
  payment_id?: string | number;
  detail?: string;
}

interface PointCreateResponse {
  id?: string;
  payment_intent_id?: string;
  status?: string;
  detail?: string;
}

interface PointStatusResponse {
  state?: string;
  payment_id?: string | number;
  rejection_reason?: string;
}

/**
 * Hook para cobrar via Mercado Pago QR (estático/dinámico).
 * Llama a POST /api/mp/qr/order para crear orden, luego polea
 * POST /api/mp/qr/validar cada 2s hasta 5min.
 */
export function useMpQrPayment(opts?: { onApproved?: (r: MpResult) => void }) {
  const [status, setStatus] = useState<MpStatus>("idle");
  const [monto, setMonto] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [paymentId, setPaymentId] = useState<string | undefined>(undefined);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const attemptRef = useRef(0);
  const extRefRef = useRef<string | undefined>(undefined);
  const cancelledRef = useRef(false);
  const onApprovedRef = useRef(opts?.onApproved);

  useEffect(() => {
    onApprovedRef.current = opts?.onApproved;
  }, [opts?.onApproved]);

  const cleanup = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    attemptRef.current = 0;
    extRefRef.current = undefined;
  }, []);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    cleanup();
    setStatus("idle");
    setError(null);
  }, [cleanup]);

  const reset = useCallback(() => {
    cancelledRef.current = false;
    cleanup();
    setStatus("idle");
    setError(null);
    setMonto(0);
    setPaymentId(undefined);
  }, [cleanup]);

  const start = useCallback(
    async (m: number) => {
      if (m <= 0) return;
      cancelledRef.current = false;
      cleanup();
      setMonto(m);
      setError(null);
      setPaymentId(undefined);
      setStatus("pending");

      try {
        const { data } = await api.post<QrCreateResponse>("/api/mp/qr/order", {
          monto: m,
        });
        extRefRef.current = data?.ext_ref || data?.external_reference;

        // Caso raro: pago instantaneo
        if (data?.status === "success" && data?.payment_id) {
          const pid = String(data.payment_id);
          setPaymentId(pid);
          setStatus("approved");
          onApprovedRef.current?.({ monto: m, mp_payment_id: pid });
          return;
        }
      } catch (e: any) {
        cleanup();
        setStatus("error");
        setError(e?.response?.data?.detail || e?.message || "Error iniciando QR");
        return;
      }

      intervalRef.current = setInterval(async () => {
        if (cancelledRef.current) {
          cleanup();
          return;
        }
        attemptRef.current += 1;

        if (attemptRef.current > MAX_ATTEMPTS) {
          cleanup();
          setStatus("timeout");
          return;
        }

        try {
          const { data } = await api.post<QrValidateResponse>(
            "/api/mp/qr/validar",
            {
              monto: m,
              ext_ref: extRefRef.current,
            }
          );
          if (data?.status === "success") {
            cleanup();
            const pid = data.payment_id ? String(data.payment_id) : undefined;
            setPaymentId(pid);
            setStatus("approved");
            onApprovedRef.current?.({ monto: m, mp_payment_id: pid });
          } else if (data?.status === "error") {
            cleanup();
            setStatus("rejected");
            setError(data.detail || "Pago rechazado");
          }
          // si "pending" o cualquier otro, seguir poleando
        } catch {
          // mantener poll, errores transitorios de red
        }
      }, POLL_INTERVAL_MS);
    },
    [cleanup]
  );

  useEffect(() => () => cleanup(), [cleanup]);

  return { status, monto, error, paymentId, start, cancel, reset };
}

/**
 * Hook para cobrar via Mercado Pago Point (POS fisico).
 * Llama a POST /api/mp/point/crear para crear intent, luego polea
 * GET /api/mp/point/estado/{intent_id} cada 2s hasta 5min.
 */
export function useMpPointPayment(opts?: {
  onApproved?: (r: MpResult) => void;
}) {
  const [status, setStatus] = useState<MpStatus>("idle");
  const [monto, setMonto] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [paymentId, setPaymentId] = useState<string | undefined>(undefined);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const attemptRef = useRef(0);
  const intentIdRef = useRef<string | undefined>(undefined);
  const cancelledRef = useRef(false);
  const onApprovedRef = useRef(opts?.onApproved);

  useEffect(() => {
    onApprovedRef.current = opts?.onApproved;
  }, [opts?.onApproved]);

  const cleanup = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    attemptRef.current = 0;
    intentIdRef.current = undefined;
  }, []);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    cleanup();
    setStatus("idle");
    setError(null);
  }, [cleanup]);

  const reset = useCallback(() => {
    cancelledRef.current = false;
    cleanup();
    setStatus("idle");
    setError(null);
    setMonto(0);
    setPaymentId(undefined);
  }, [cleanup]);

  const start = useCallback(
    async (m: number) => {
      if (m <= 0) return;
      cancelledRef.current = false;
      cleanup();
      setMonto(m);
      setError(null);
      setPaymentId(undefined);
      setStatus("pending");

      let intentId: string | undefined;
      try {
        const { data } = await api.post<PointCreateResponse>(
          "/api/mp/point/crear",
          { monto: m }
        );
        intentId = data?.id || data?.payment_intent_id;
        if (!intentId) {
          throw new Error(data?.detail || "No se obtuvo intent ID de Point");
        }
        intentIdRef.current = intentId;
      } catch (e: any) {
        cleanup();
        setStatus("error");
        setError(
          e?.response?.data?.detail || e?.message || "Error iniciando Point"
        );
        return;
      }

      intervalRef.current = setInterval(async () => {
        if (cancelledRef.current) {
          cleanup();
          return;
        }
        attemptRef.current += 1;

        if (attemptRef.current > MAX_ATTEMPTS) {
          cleanup();
          setStatus("timeout");
          return;
        }

        try {
          const { data } = await api.get<PointStatusResponse>(
            `/api/mp/point/estado/${intentId}`
          );
          const state = data?.state;
          if (state === "FINISHED" || state === "SUCCESS") {
            cleanup();
            const pid = data?.payment_id ? String(data.payment_id) : undefined;
            setPaymentId(pid);
            setStatus("approved");
            onApprovedRef.current?.({ monto: m, mp_payment_id: pid });
          } else if (
            state === "ABANDONED" ||
            state === "CANCELLED" ||
            state === "REJECTED"
          ) {
            cleanup();
            setStatus("rejected");
            setError(data?.rejection_reason || "Point rechazado o cancelado");
          }
        } catch {
          // mantener poll
        }
      }, POLL_INTERVAL_MS);
    },
    [cleanup]
  );

  useEffect(() => () => cleanup(), [cleanup]);

  return { status, monto, error, paymentId, start, cancel, reset };
}

/**
 * Hook para detectar si MP está configurado en el backend.
 * Si /api/mp/status responde 503 (o falla), MP no está disponible.
 */
export function useMpAvailability() {
  const [available, setAvailable] = useState<boolean>(true);
  const [checked, setChecked] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/api/mp/status", {
          validateStatus: () => true,
        });
        if (!cancelled) {
          setAvailable(res.status !== 503 && res.status < 500);
          setChecked(true);
        }
      } catch {
        if (!cancelled) {
          setAvailable(false);
          setChecked(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { available, checked };
}
